import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Car, Shield, CheckCircle, Star, Sparkles, 
  Calendar, MapPin, History, CreditCard, Users, 
  ClipboardCheck, ArrowRight, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import PublicLegalFooter from '@/components/PublicLegalFooter';

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* ========== HERO SECTION ========== */}
      <div className="bg-[#0B1F3A] text-white py-20 lg:py-28">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            {/* Logo + Nome da Marca (Layout Horizontal) */}
            <div className="flex items-center justify-center gap-[10px] mb-8">
              <Car className="w-8 h-8 text-white" />
              <span className="text-[26px] font-bold uppercase text-white" style={{ letterSpacing: '1px' }}>
                JATOO
              </span>
            </div>
            
            {/* Título principal */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" data-testid="landing-title">
              Lavagem automotiva inteligente por assinatura
            </h1>
            
            {/* Subtítulo */}
            <p className="text-xl md:text-2xl text-white/80 max-w-2xl mx-auto mb-10">
              Agende lavagens com praticidade através da rede parceira JATOO.
            </p>
            
            {/* Botões principais - mantendo rotas existentes */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                data-testid="client-login-btn"
                onClick={() => navigate('/client/login')} 
                className="bg-[#2563EB] hover:bg-[#2563EB]/90 text-white px-8 py-6 text-lg font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transition-all"
              >
                <Car className="w-5 h-5 mr-2" />
                Quero lavar meu carro
              </Button>
              <Button 
                data-testid="provider-login-btn"
                onClick={() => navigate('/provider/login')}
                variant="outline"
                className="border-2 border-white/40 text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold transition-all"
              >
                <Shield className="w-5 h-5 mr-2" />
                Quero ser prestador
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ========== CARDS EXPLICATIVOS (sem botões) ========== */}
      <div className="container mx-auto px-4 py-16 lg:py-20">
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          
          {/* Card Para Clientes */}
          <Card className="p-8 hover:shadow-xl transition-all border-[#E2E8F0] hover:border-[#2563EB]/30">
            <div className="bg-[#2563EB]/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
              <Car className="w-8 h-8 text-[#2563EB]" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-[#0B1F3A]">Para Clientes</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#16A34A] mt-0.5 flex-shrink-0" />
                <span className="text-slate-600">Lavagens incluídas no plano</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#16A34A] mt-0.5 flex-shrink-0" />
                <span className="text-slate-600">Agendamento rápido pelo aplicativo</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#16A34A] mt-0.5 flex-shrink-0" />
                <span className="text-slate-600">Rede de lava-jatos parceiros</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#16A34A] mt-0.5 flex-shrink-0" />
                <span className="text-slate-600">Controle do histórico de serviços</span>
              </li>
            </ul>
          </Card>

          {/* Card Para Prestadores */}
          <Card className="p-8 hover:shadow-xl transition-all border-[#E2E8F0] hover:border-[#16A34A]/30">
            <div className="bg-[#16A34A]/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
              <Shield className="w-8 h-8 text-[#16A34A]" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-[#0B1F3A]">Para Prestadores</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#16A34A] mt-0.5 flex-shrink-0" />
                <span className="text-slate-600">Receba novos clientes da plataforma</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#16A34A] mt-0.5 flex-shrink-0" />
                <span className="text-slate-600">Ganhe por cada lavagem realizada</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#16A34A] mt-0.5 flex-shrink-0" />
                <span className="text-slate-600">Controle sua agenda de serviços</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#16A34A] mt-0.5 flex-shrink-0" />
                <span className="text-slate-600">Receba repasses pela plataforma</span>
              </li>
            </ul>
          </Card>
        </div>
      </div>

      {/* ========== SEÇÃO COMO FUNCIONA ========== */}
      <div className="bg-white py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-[#0B1F3A]">Como funciona</h2>
            <p className="text-slate-500 max-w-xl mx-auto">
              Simples e prático em apenas três passos
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Passo 1 */}
            <div className="text-center">
              <div className="bg-[#2563EB] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-500/30">
                <CreditCard className="w-7 h-7 text-white" />
              </div>
              <div className="bg-[#2563EB]/10 text-[#2563EB] text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">
                PASSO 1
              </div>
              <h3 className="text-lg font-bold text-[#0B1F3A] mb-2">Escolha um plano</h3>
              <p className="text-slate-500">
                Escolha um plano mensal de lavagem que se encaixa nas suas necessidades
              </p>
            </div>
            
            {/* Passo 2 */}
            <div className="text-center">
              <div className="bg-[#2563EB] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-500/30">
                <Calendar className="w-7 h-7 text-white" />
              </div>
              <div className="bg-[#2563EB]/10 text-[#2563EB] text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">
                PASSO 2
              </div>
              <h3 className="text-lg font-bold text-[#0B1F3A] mb-2">Agende a lavagem</h3>
              <p className="text-slate-500">
                Agende a lavagem pelo aplicativo no horário que preferir
              </p>
            </div>
            
            {/* Passo 3 */}
            <div className="text-center">
              <div className="bg-[#2563EB] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-500/30">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <div className="bg-[#2563EB]/10 text-[#2563EB] text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">
                PASSO 3
              </div>
              <h3 className="text-lg font-bold text-[#0B1F3A] mb-2">Leve seu carro</h3>
              <p className="text-slate-500">
                Leve seu carro a um lava-jato parceiro e aproveite o serviço
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ========== SEÇÃO PLANOS ========== */}
      <div className="container mx-auto px-4 py-16 lg:py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-[#0B1F3A]">Nossos Planos</h2>
          <p className="text-slate-500 max-w-2xl mx-auto">
            Escolha o plano ideal para você. Valores e serviços variam conforme o tipo do veículo.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {/* Plano Básico */}
          <Card className="p-6 lg:p-8 hover:shadow-xl transition-all border-[#E2E8F0] flex flex-col">
            <div className="flex-1">
              <h3 className="text-xl lg:text-2xl font-bold mb-2 text-[#0B1F3A]">Plano Básico</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#16A34A]" />
                  Lavagens completas incluídas
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#16A34A]" />
                  Duchas simples incluídas
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#16A34A]" />
                  Agendamento pelo app
                </li>
              </ul>
            </div>
            <Button 
              onClick={() => navigate('/client/login')}
              variant="outline"
              className="w-full border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB]/10"
            >
              Escolher plano
            </Button>
          </Card>

          {/* Plano Intermediário - Destaque */}
          <Card className="p-6 lg:p-8 border-2 border-[#2563EB] shadow-xl hover:shadow-2xl transition-all relative flex flex-col transform hover:scale-[1.02]">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-[#2563EB] text-white text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1 shadow-lg">
                <Star className="w-3 h-3" />
                MAIS POPULAR
              </span>
            </div>
            <div className="flex-1 mt-2">
              <h3 className="text-xl lg:text-2xl font-bold mb-2 text-[#0B1F3A]">Plano Intermediário</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#16A34A]" />
                  Mais lavagens completas
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#16A34A]" />
                  Mais duchas simples
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#16A34A]" />
                  Agendamento prioritário
                </li>
              </ul>
            </div>
            <Button 
              onClick={() => navigate('/client/login')}
              className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90 shadow-lg"
            >
              Escolher plano
            </Button>
          </Card>

          {/* Plano Premium */}
          <Card className="p-6 lg:p-8 hover:shadow-xl transition-all border-[#E2E8F0] flex flex-col">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-[#F97316]" />
                <span className="text-sm font-bold text-[#F97316]">PREMIUM</span>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold mb-2 text-[#0B1F3A]">Plano Premium</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#16A34A]" />
                  Máximo de lavagens
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#16A34A]" />
                  Máximo de duchas
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#16A34A]" />
                  Benefícios exclusivos
                </li>
              </ul>
            </div>
            <Button 
              onClick={() => navigate('/client/login')}
              variant="outline"
              className="w-full border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB]/10"
            >
              Escolher plano
            </Button>
          </Card>
        </div>
      </div>

      {/* ========== SEÇÃO TECNOLOGIA ========== */}
      <div className="bg-[#0B1F3A] py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-white">Tecnologia da plataforma</h2>
              <p className="text-white/70 max-w-xl mx-auto">
                Uma plataforma completa para conectar motoristas e lava-jatos
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-4 bg-white/5 p-5 rounded-xl">
                <div className="bg-[#2563EB] p-3 rounded-lg flex-shrink-0">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Sistema de agendamento inteligente</h3>
                  <p className="text-white/60 text-sm">Agende lavagens com base na disponibilidade em tempo real</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-white/5 p-5 rounded-xl">
                <div className="bg-[#16A34A] p-3 rounded-lg flex-shrink-0">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Rede de prestadores verificados</h3>
                  <p className="text-white/60 text-sm">Todos os lava-jatos passam por verificação de qualidade</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-white/5 p-5 rounded-xl">
                <div className="bg-[#F97316] p-3 rounded-lg flex-shrink-0">
                  <History className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Controle de histórico de serviços</h3>
                  <p className="text-white/60 text-sm">Acompanhe todas as lavagens realizadas pelo app</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-white/5 p-5 rounded-xl">
                <div className="bg-[#2563EB] p-3 rounded-lg flex-shrink-0">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Plataforma segura de pagamentos</h3>
                  <p className="text-white/60 text-sm">Pagamentos processados com segurança via Stripe</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== CTA FINAL ========== */}
      <div className="bg-white py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto">
            <div className="bg-[#2563EB]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <Zap className="w-8 h-8 text-[#2563EB]" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-[#0B1F3A]">
              Comece agora e escolha seu plano de lavagem
            </h2>
            <p className="text-slate-500 mb-8">
              Cadastre-se gratuitamente e escolha o plano ideal para manter seu carro sempre limpo.
            </p>
            <Button 
              onClick={() => navigate('/client/login')}
              className="bg-[#2563EB] hover:bg-[#2563EB]/90 text-white px-10 py-6 text-lg font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl transition-all"
              data-testid="cta-final-btn"
            >
              Começar agora
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* ========== FOOTER ========== */}
      <div className="bg-[#F8FAFC] border-t border-[#E2E8F0] py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-[#0B1F3A] p-2 rounded-lg">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-[#0B1F3A]">JATOO</span>
            </div>
            <p className="text-slate-500 text-sm">
              © 2026 JATOO. Todos os direitos reservados.
            </p>
          </div>
          <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
            <PublicLegalFooter />
          </div>
        </div>
      </div>
    </div>
  );
}
